import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import {
  BookOpen, CalendarDays, Sparkles, HandHeart, NotebookPen, X,
  ArrowRight, ArrowLeft, Compass, Bookmark, Flame,
} from "lucide-react";

const STEPS = [
  {
    icon: Compass,
    kicker: { hi: "स्वागतम्", en: "Welcome" },
    title: { hi: "धर्मार्थ में आपका स्वागत है", en: "Welcome to Dharmartha" },
    body: {
      hi: "सनातन ज्ञान का एक शांत, विज्ञापन-रहित डिजिटल आश्रय — कथाएँ, मंत्र, स्तोत्र, पूजा विधि, पंचांग और बहुत कुछ। आइए, दो मिनट में एक झलक देखें।",
      en: "A calm, ad-free digital home for Sanatana knowledge — kathas, mantras, stotras, puja vidhis, panchang and more. Here's a two-minute tour of what you can do.",
    },
    points: [],
    cta: null,
  },
  {
    icon: BookOpen,
    kicker: { hi: "ज्ञानकोश", en: "Knowledge Hub" },
    title: { hi: "पढ़ें और खोजें", en: "Read & explore" },
    body: {
      hi: "देवनागरी मूल पाठ, लिप्यंतरण और अर्थ के साथ प्रामाणिक सामग्री। श्रेणी के अनुसार ब्राउज़ करें या खोजें।",
      en: "Authentic content with Devanagari source text, transliteration and meaning. Browse by category or search across the whole library.",
    },
    points: [
      { hi: "कथाएँ, मंत्र, सूक्त, स्तोत्र", en: "Kathas, Mantras, Suktas, Stotras" },
      { hi: "पूजा विधि एवं सचित्र ग्रंथ", en: "Pooja Vidhis & illustrated scriptures" },
    ],
    cta: { to: "/explore", hi: "अन्वेषण करें", en: "Explore knowledge" },
  },
  {
    icon: CalendarDays,
    kicker: { hi: "पंचांग", en: "Panchangam" },
    title: { hi: "आज का पंचांग एवं मुहूर्त", en: "Today's Panchangam & Muhurta" },
    body: {
      hi: "आपके स्थान के अनुसार सटीक तिथि, नक्षत्र, योग, चौघड़िया और शुभ मुहूर्त — प्रतिदिन।",
      en: "Precise tithi, nakshatra, yoga, choghadiya and auspicious timings for your exact location — every day.",
    },
    points: [
      { hi: "सूर्योदय/सूर्यास्त एवं चौघड़िया", en: "Sunrise/sunset & Choghadiya" },
      { hi: "पर्व एवं व्रत कैलेंडर", en: "Festival & vrata calendar" },
    ],
    cta: { to: "/panchang", hi: "पंचांग देखें", en: "View Panchangam" },
  },
  {
    icon: NotebookPen,
    kicker: { hi: "साधना", en: "Practice & Track" },
    title: { hi: "अपनी साधना सहेजें", en: "Track your practice" },
    body: {
      hi: "जप गिनें, पढ़ने की प्रगति देखें, प्रिय पृष्ठ सहेजें और किसी भी पृष्ठ पर निजी टिप्पणियाँ जोड़ें।",
      en: "Count your japa, track reading progress, bookmark favourites, and add private notes on any page.",
    },
    points: [
      { hi: "जप माला काउंटर एवं स्ट्रीक", en: "Japa mala counter & streaks" },
      { hi: "बुकमार्क एवं मेरी टिप्पणियाँ", en: "Bookmarks & My Notes" },
    ],
    cta: { to: "/japa", hi: "जप आरंभ करें", en: "Start a Japa" },
  },
  {
    icon: HandHeart,
    kicker: { hi: "सेवाएँ", en: "Services" },
    title: { hi: "सेवाएँ एवं मार्गदर्शन", en: "Services & guidance" },
    body: {
      hi: "पूजा बुकिंग, ज्योतिष परामर्श और अंकशास्त्र कैलकुलेटर — एक ही स्थान पर।",
      en: "Pooja booking, astrology guidance and a numerology calculator — all in one place.",
    },
    points: [
      { hi: "पूजा एवं अनुष्ठान बुकिंग", en: "Pooja & ritual booking" },
      { hi: "अंकशास्त्र कैलकुलेटर", en: "Numerology calculator" },
    ],
    cta: { to: "/services", hi: "सेवाएँ देखें", en: "See services" },
  },
  {
    icon: Sparkles,
    kicker: { hi: "तैयार!", en: "All set" },
    title: { hi: "आप तैयार हैं", en: "You're all set" },
    body: {
      hi: "जहाँ मन करे वहाँ से आरंभ करें। आप कभी भी मेनू से सब कुछ पा सकते हैं।",
      en: "Begin wherever your heart leads. You can reach everything anytime from the menu.",
    },
    points: [],
    cta: null,
    finish: true,
  },
];

const QUICK = [
  { to: "/explore", icon: BookOpen, hi: "ज्ञानकोश", en: "Knowledge" },
  { to: "/panchang", icon: CalendarDays, hi: "पंचांग", en: "Panchangam" },
  { to: "/japa", icon: Flame, hi: "जप", en: "Japa" },
  { to: "/bookmarks", icon: Bookmark, hi: "बुकमार्क", en: "Bookmarks" },
];

export default function WelcomeTour() {
  const { user, completeOnboarding } = useAuth();
  const { pick, isDeva } = useLang();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  const eligible = user && typeof user === "object" && user.onboarded === false && !pathname.startsWith("/admin");

  useEffect(() => {
    if (eligible) {
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, [eligible]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const finish = () => { setOpen(false); completeOnboarding(); };
  const step = STEPS[i];
  const last = i === STEPS.length - 1;
  const Icon = step.icon;

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-0 sm:p-4" data-testid="welcome-tour">
      <div className="absolute inset-0 bg-ink/75 backdrop-blur-md" />
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.35 }}
        className="relative flex h-full w-full max-w-lg flex-col border-[var(--gold-muted)] bg-surface shadow-2xl sm:h-auto sm:max-h-[92vh] sm:border"
      >
        <button onClick={finish} data-testid="welcome-tour-skip" aria-label="Skip tour"
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full text-ink-3 transition-colors hover:bg-parchment hover:text-ink">
          <X className="h-5 w-5" />
        </button>

        <div className="flex-1 overflow-y-auto px-7 py-10 md:px-10">
          {/* decorative motif */}
          <div className="mb-6 flex h-16 w-16 items-center justify-center border border-[var(--gold-muted)] bg-[rgba(176,141,87,0.08)] text-saffron">
            <Icon className="h-8 w-8" strokeWidth={1.5} />
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={i}
              initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.28 }}>
              <p className="overline-label text-gold">{pick(step.kicker.hi, step.kicker.en)}</p>
              <h2 className={`mt-2 text-3xl text-ink deva-lh md:text-4xl ${isDeva ? "font-deva" : "font-serif-latin"}`} data-testid="welcome-tour-title">
                {pick(step.title.hi, step.title.en)}
              </h2>
              <p className="mt-4 text-[15px] leading-relaxed text-ink-2 md:text-base">{pick(step.body.hi, step.body.en)}</p>

              {step.points.length > 0 && (
                <ul className="mt-5 space-y-2.5">
                  {step.points.map((p, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-[15px] text-ink-2">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-saffron" />
                      {pick(p.hi, p.en)}
                    </li>
                  ))}
                </ul>
              )}

              {step.cta && (
                <Link to={step.cta.to} onClick={finish} data-testid="welcome-tour-cta"
                  className="mt-6 inline-flex items-center gap-2 border border-[var(--gold-muted)] px-4 py-2 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
                  {pick(step.cta.hi, step.cta.en)} <ArrowRight className="h-4 w-4" />
                </Link>
              )}

              {step.finish && (
                <div className="mt-6 grid grid-cols-2 gap-3">
                  {QUICK.map((q) => (
                    <Link key={q.to} to={q.to} onClick={finish} data-testid={`welcome-quick-${q.to.replace("/", "")}`}
                      className="group flex items-center gap-3 border border-line bg-parchment/40 px-4 py-3 transition-colors hover:border-saffron">
                      <q.icon className="h-4 w-4 shrink-0 text-saffron" />
                      <span className="text-sm text-ink-2 transition-colors group-hover:text-ink">{pick(q.hi, q.en)}</span>
                    </Link>
                  ))}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between gap-4 border-t border-line bg-surface/95 px-7 py-4 md:px-10">
          <div className="flex items-center gap-1.5" aria-hidden="true">
            {STEPS.map((_, idx) => (
              <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-saffron" : "w-1.5 bg-line"}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <button onClick={() => setI((v) => v - 1)} data-testid="welcome-tour-back"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-ink-2 transition-colors hover:text-ink">
                <ArrowLeft className="h-4 w-4" /> {pick("पीछे", "Back")}
              </button>
            )}
            {last ? (
              <button onClick={finish} data-testid="welcome-tour-finish"
                className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
                {pick("आरंभ करें", "Start exploring")} <Sparkles className="h-4 w-4" />
              </button>
            ) : (
              <button onClick={() => setI((v) => v + 1)} data-testid="welcome-tour-next"
                className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)]">
                {pick("आगे", "Next")} <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
  return createPortal(modal, document.body);
}
